/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */

/**
 * ActorError is the ONLY error type rejected by {@link Actor#callPromise}.
 *
 * It normalizes heterogeneous failures into a stable, idiomatic JS Error shape while preserving
 * the underlying RPC error event when available.
 *
 * Stable fields (always present as properties):
 * - kind: "timeout" | "transport" | "remote" | "protocol" | "internal"
 * - code: string (stable code, or propagated from remote event.detail.code when present)
 * - actorEvent: ActorEvent|null (set when a reply error ActorEvent('e') exists; includes synthetic local errors)
 * - callId: string|null
 * - callName: string|null
 * - addresses: Addresses|null
 * - retryable: boolean
 * - cause: Error|undefined (underlying error if available, e.g. event.localError or transport error)
 *
 * Notes:
 * - For remote errors, `actorEvent` is the received error reply (instruction 'e').
 * - For timeouts and local send failures, `actorEvent` is a synthetic error reply created by ActorEvent.call().
 */
export class ActorError extends Error {

    /**
     * @param {string} message
     * @param {Object} [opts]
     * @param {string} [opts.kind]
     * @param {string} [opts.code]
     * @param {Object|null} [opts.actorEvent]
     * @param {string|null} [opts.callId]
     * @param {string|null} [opts.callName]
     * @param {Object|null} [opts.addresses]
     * @param {boolean} [opts.retryable]
     * @param {Error|undefined} [opts.cause]
     */
    constructor(message, opts = {}) {
        super(message);

        /**
         * Keep a stable error name for downstream classification and telemetry.
         * @type {string}
         */
        this.name = "ActorError";

        /**
         * @type {"timeout"|"transport"|"remote"|"protocol"|"internal"}
         */
        this.kind = typeof opts.kind === "string" ? opts.kind : "internal";

        /**
         * @type {string}
         */
        this.code = typeof opts.code === "string" ? opts.code : ActorError._defaultCodeForKind(this.kind);

        /**
         * Underlying ActorEvent('e') when available (remote or synthetic).
         * @type {Object|null}
         */
        this.actorEvent = (opts.actorEvent !== undefined) ? opts.actorEvent : null;

        /**
         * @type {string|null}
         */
        this.callId = (typeof opts.callId === "string" && opts.callId !== "") ? opts.callId : null;

        /**
         * @type {string|null}
         */
        this.callName = (typeof opts.callName === "string" && opts.callName !== "") ? opts.callName : null;

        /**
         * @type {Object|null}
         */
        this.addresses = (opts.addresses !== undefined) ? opts.addresses : null;

        /**
         * Retry hint is intentionally conservative: only timeouts/transports are assumed retryable unless explicitly stated.
         * @type {boolean}
         */
        this.retryable = (typeof opts.retryable === "boolean") ? opts.retryable : (this.kind === "timeout" || this.kind === "transport");

        /**
         * Preserve the originating Error when available to keep stack traces actionable without changing the ActorError surface.
         * @type {Error|undefined}
         */
        this.cause = (opts.cause instanceof Error) ? opts.cause : undefined;
    }

    /**
     * Creates an ActorError from an ActorEvent error reply (instruction 'e').
     *
     * Kind inference:
     * - If event.detail.kind is a string => use it.
     * - Else infer from event.detail.stage:
     *      - "timeout" => "timeout"
     *      - "send"    => "transport"
     *      - otherwise => "remote"
     *
     * Message building:
     * - Uses event.type and best-effort extraction of a textual message from event.detail.
     *
     * @param {ActorEvent} event An ActorEvent with instruction 'e'
     * @returns {ActorError}
     */
    static fromActorEventError(event) {
        const detail = event ? event.detail : undefined;

        const kind = ActorError._inferKindFromDetail(detail);
        const code = ActorError._inferCodeFromDetail(kind, detail);
        const retryable = ActorError._inferRetryableFromDetail(kind, detail);

        const extracted = ActorError._bestEffortMessageFromDetail(detail);
        const callName = (event && typeof event.type === "string") ? event.type : null;

        const message = callName
            ? `${callName} ${kind} error: ${extracted}`
            : `${kind} error: ${extracted}`;

        const cause = (event && event.localError instanceof Error) ? event.localError : undefined;

        return new ActorError(message, {
            kind: kind,
            code: code,
            actorEvent: event || null,
            callId: event && typeof event.callId === "string" ? event.callId : null,
            callName: callName,
            addresses: event && event.addresses !== undefined ? event.addresses : null,
            retryable: retryable,
            cause: cause
        });
    }

    /**
     * Creates an ActorError from a local transport/encryption/framework error
     * where an ActorEvent error reply may not exist.
     *
     * @param {*} err
     * @param {Object} [ctx]
     * @param {string} [ctx.callId]
     * @param {string} [ctx.callName]
     * @param {Object} [ctx.addresses]
     * @param {string} [ctx.stage]     Optional stage (e.g. "send", "internal", "protocol")
     * @param {string} [ctx.kind]      Optional explicit kind override
     * @param {string} [ctx.code]      Optional explicit code override
     * @param {boolean} [ctx.retryable]
     * @returns {ActorError}
     */
    static fromTransportError(err, ctx = {}) {
        if (err instanceof ActorError) {
            return err;
        }

        const callId = (ctx && typeof ctx.callId === "string" && ctx.callId !== "") ? ctx.callId : null;
        const callName = (ctx && typeof ctx.callName === "string" && ctx.callName !== "") ? ctx.callName : null;
        const addresses = (ctx && ctx.addresses !== undefined) ? ctx.addresses : null;

        const stage = (ctx && typeof ctx.stage === "string") ? ctx.stage : undefined;

        let kind;
        if (ctx && typeof ctx.kind === "string" && ctx.kind !== "") {
            kind = ctx.kind;
        } else if (stage === "internal") {
            kind = "internal";
        } else if (stage === "protocol") {
            kind = "protocol";
        } else {
            kind = "transport";
        }

        let code;
        if (ctx && typeof ctx.code === "string" && ctx.code !== "") {
            code = ctx.code;
        } else if (kind === "transport" && stage === "send") {
            code = "E_TRANSPORT_SEND";
        } else {
            code = ActorError._defaultCodeForKind(kind);
        }

        let retryable;
        if (ctx && typeof ctx.retryable === "boolean") {
            retryable = ctx.retryable;
        } else {
            retryable = (kind === "timeout" || kind === "transport");
        }

        const baseMsg = ActorError._bestEffortMessageFromAny(err);
        const message = callName
            ? `${callName} ${kind} error: ${baseMsg}`
            : `${kind} error: ${baseMsg}`;

        const cause = (err instanceof Error) ? err : undefined;

        return new ActorError(message, {
            kind: kind,
            code: code,
            actorEvent: null,
            callId: callId,
            callName: callName,
            addresses: addresses,
            retryable: retryable,
            cause: cause
        });
    }

    /////////////////////////
    // Internal helpers
    /////////////////////////

    /**
     * @param {*} detail
     * @returns {"timeout"|"transport"|"remote"|"protocol"|"internal"}
     * @private
     */
    static _inferKindFromDetail(detail) {
        if (detail && typeof detail === "object") {
            if (typeof detail.kind === "string" && detail.kind.trim() !== "") {
                return detail.kind;
            }
            if (typeof detail.stage === "string") {
                if (detail.stage === "timeout") {
                    return "timeout";
                }
                if (detail.stage === "send") {
                    return "transport";
                }
            }
            return "remote";
        }
        return "remote";
    }

    /**
     * @param {string} kind
     * @param {*} detail
     * @returns {string}
     * @private
     */
    static _inferCodeFromDetail(kind, detail) {
        if (detail && typeof detail === "object") {
            if (typeof detail.code === "string" && detail.code.trim() !== "") {
                return detail.code;
            }
            if (kind === "transport" && detail.stage === "send") {
                return "E_TRANSPORT_SEND";
            }
        }
        return ActorError._defaultCodeForKind(kind);
    }

    /**
     * @param {string} kind
     * @param {*} detail
     * @returns {boolean}
     * @private
     */
    static _inferRetryableFromDetail(kind, detail) {
        if (detail && typeof detail === "object" && typeof detail.retryable === "boolean") {
            return detail.retryable;
        }
        return (kind === "timeout" || kind === "transport");
    }

    /**
     * @param {*} detail
     * @returns {string}
     * @private
     */
    static _bestEffortMessageFromDetail(detail) {
        if (typeof detail === "string" && detail.trim() !== "") {
            return detail;
        }
        if (detail && typeof detail === "object") {
            if (typeof detail.message === "string" && detail.message.trim() !== "") {
                return detail.message;
            }
            if (typeof detail.error === "string" && detail.error.trim() !== "") {
                return detail.error;
            }
            if (typeof detail.reason === "string" && detail.reason.trim() !== "") {
                return detail.reason;
            }
        }
        return "opaque error";
    }

    /**
     * @param {*} err
     * @returns {string}
     * @private
     */
    static _bestEffortMessageFromAny(err) {
        if (err instanceof Error) {
            return (typeof err.message === "string" && err.message.trim() !== "") ? err.message : "Error";
        }
        if (typeof err === "string") {
            return err;
        }
        try {
            return String(err);
        } catch (_) {
            return "unknown error";
        }
    }

    /**
     * @param {string} kind
     * @returns {string}
     * @private
     */
    static _defaultCodeForKind(kind) {
        switch (kind) {
            case "timeout":
                return "E_TIMEOUT";
            case "transport":
                return "E_TRANSPORT";
            case "remote":
                return "E_REMOTE";
            case "protocol":
                return "E_PROTOCOL";
            case "internal":
            default:
                return "E_INTERNAL";
        }
    }
}